import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Shield, Users, Lightbulb, BarChart } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';

const Services = () => {
  const { t, loading } = useComponentTranslations('services');

  if (loading) {
    return (
      <section id="services" className="py-20 bg-white scroll-mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse mb-4"></div>
            <div className="h-6 bg-gray-200 rounded-lg animate-pulse max-w-2xl mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse mb-6"></div>
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const services = [
    {
      icon: Target,
      title: t('consulting_title', 'Strategic Consulting'),
      description: t('consulting_desc', 'Expert guidance for complex business challenges and strategic decision-making processes.'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: TrendingUp,
      title: t('operations_title', 'Operations Management'),
      description: t('operations_desc', 'Streamlined processes and operational excellence to maximize efficiency and performance.'),
      color: 'from-teal-500 to-green-500'
    },
    {
      icon: Shield,
      title: t('governance_title', 'Corporate Governance'),
      description: t('governance_desc', 'Comprehensive governance frameworks ensuring compliance and stakeholder value.'),
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: Users,
      title: t('leadership_title', 'Leadership Development'),
      description: t('leadership_desc', 'Building strong leadership capabilities and organizational culture transformation.'),
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Lightbulb,
      title: t('innovation_title', 'Innovation Strategy'),
      description: t('innovation_desc', 'Driving innovation and digital transformation initiatives for competitive advantage.'),
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: BarChart,
      title: t('analytics_title', 'Performance Analytics'),
      description: t('analytics_desc', 'Data-driven insights and performance metrics to guide strategic decisions.'),
      color: 'from-pink-500 to-purple-500'
    }
  ];

  return (
    <section id="services" className="py-20 bg-white scroll-mt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
          >
            {t('title', 'Our Services')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('subtitle', 'Comprehensive solutions designed to drive your business forward')}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
            >
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-8 md:p-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('cta_title', 'Ready to Transform Your Business?')}
            </h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t('cta_subtitle', 'Let us help you achieve operational excellence and strategic growth.')}
            </p>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300 transform hover:scale-105 shadow-lg"
            >
              {t('cta_button', 'Get Started Today')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;